import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Radio, UserCheck } from "lucide-react";
import { useLang } from "@/context/LangContext";

const TR = {
  sk: {
    peopleOnline: "ľudí online",
    registeredMembers: "registrovaných členov",
    noChannels: "Nikto momentálne nie je v žiadnom kanáli",
    liveChannels: "Live v kanáloch",
    channels: {
      general: "Generál",
      tig: "TIG",
      orbital: "Orbital",
      pipefitter: "Potrubári",
      prep: "Prípravári",
      jobs: "Ponuka práce",
    },
  },
  en: {
    peopleOnline: "people online",
    registeredMembers: "registered members",
    noChannels: "Nobody is in any channel right now",
    liveChannels: "Live in channels",
    channels: {
      general: "General",
      tig: "TIG",
      orbital: "Orbital",
      pipefitter: "Pipefitters",
      prep: "Preppers",
      jobs: "Jobs",
    },
  },
  cs: {
    peopleOnline: "lidí online",
    registeredMembers: "registrovaných členů",
    noChannels: "Aktuálně není nikdo v žádném kanálu",
    liveChannels: "Live v kanálech",
    channels: {
      general: "Generál",
      tig: "TIG",
      orbital: "Orbital",
      pipefitter: "Potrubáři",
      prep: "Přípraváři",
      jobs: "Nabídka práce",
    },
  },
  de: {
    peopleOnline: "Leute online",
    registeredMembers: "registrierte Mitglieder",
    noChannels: "Gerade ist niemand in einem Kanal",
    liveChannels: "Live in Kanälen",
    channels: {
      general: "Allgemein",
      tig: "WIG",
      orbital: "Orbital",
      pipefitter: "Rohrleger",
      prep: "Vorbereiter",
      jobs: "Stellenangebote",
    },
  },
  pl: {
    peopleOnline: "ludzi online",
    registeredMembers: "zarejestrowanych członków",
    noChannels: "Nikogo nie ma teraz w żadnym kanale",
    liveChannels: "Live w kanałach",
    channels: {
      general: "Ogólny",
      tig: "TIG",
      orbital: "Orbital",
      pipefitter: "Rurociągowcy",
      prep: "Przygotowywacze",
      jobs: "Oferty pracy",
    },
  },
  fr: {
    peopleOnline: "personnes en ligne",
    registeredMembers: "membres inscrits",
    noChannels: "Personne n'est actuellement dans aucun canal",
    liveChannels: "Live dans les canaux",
    channels: {
      general: "Général",
      tig: "TIG",
      orbital: "Orbital",
      pipefitter: "Tuyauteurs",
      prep: "Préparateurs",
      jobs: "Offres d'emploi",
    },
  },
};

/**
 * Live online users counter.
 * - mode="total": one big number + total label (online)
 * - mode="registered": count of registered members (NOT online — total community size)
 * - mode="channels": list of channels with their online counts + jump button
 */
export default function OnlineCounter({ mode = "total" }) {
  const { lang } = useLang();
  const t = TR[lang] || TR.sk;
  const [data, setData] = useState(null);
  const [registered, setRegistered] = useState(null);

  useEffect(() => {
    if (mode === "registered") {
      const fetchReg = () => api.get("/launch-stats").then((r) => setRegistered(r.data?.registered_count ?? 0)).catch(() => {});
      fetchReg();
      const id = setInterval(fetchReg, 60000);
      return () => clearInterval(id);
    }
    const fetchOnce = () => api.get("/presence/online").then((r) => setData(r.data)).catch(() => {});
    fetchOnce();
    const id = setInterval(fetchOnce, 30000);
    return () => clearInterval(id);
  }, [mode]);

  if (mode === "registered") {
    if (registered === null) return null;
    return (
      <div data-testid="registered-total" className="inline-flex items-center gap-2 px-3 py-1.5 border border-gold-700/40 bg-gold-950/10 text-xs">
        <UserCheck className="w-3 h-3 text-gold-400" />
        <span className="font-mono uppercase tracking-wider text-gold-400">
          <span className="tabular-nums">{registered}</span> {t.registeredMembers}
        </span>
      </div>
    );
  }

  if (!data) return null;

  if (mode === "total") {
    return (
      <div data-testid="online-total" className="inline-flex items-center gap-2 px-3 py-1.5 border border-green-700/40 bg-green-950/20 text-xs">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
        </span>
        <Users className="w-3 h-3 text-green-400" />
        <span className="font-mono uppercase tracking-wider text-green-400">
          <span className="tabular-nums">{data.total}</span> {t.peopleOnline}
        </span>
      </div>
    );
  }

  if (mode === "channels") {
    const entries = Object.entries(data.per_channel || {}).filter(([, n]) => n > 0);
    if (entries.length === 0) {
      return (
        <div className="text-xs text-zinc-600 font-mono uppercase tracking-wider" data-testid="online-channels-empty">
          {t.noChannels}
        </div>
      );
    }
    return (
      <div className="space-y-2" data-testid="online-channels">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{t.liveChannels}</div>
        {entries.map(([ch, n]) => (
          <Link to={`/community?channel=${ch}`} key={ch} data-testid={`online-channel-${ch}`}
            className="flex items-center justify-between p-2 border border-zinc-800 hover:border-gold-700/60 hover:bg-zinc-900/40 transition-colors text-xs group">
            <span className="flex items-center gap-2">
              <Radio className="w-3 h-3 text-green-500" />
              {t.channels[ch] || ch}
            </span>
            <span className="flex items-center gap-1 font-mono text-zinc-400 group-hover:text-gold-400">
              <Users className="w-3 h-3" /> {n}
            </span>
          </Link>
        ))}
      </div>
    );
  }

  return null;
}
